/**
 * ParquetPreview — the indexed, queryable table view of a structured file.
 *
 * This is the *processed* representation: the extraction pipeline strips any
 * report preamble and builds a typed Parquet table, which this component
 * filters/sorts/paginates via DuckDB. It lives in the Indexed Data tab
 * because it reflects what was indexed, not the raw file.
 */

import { Database, Loader2 } from 'lucide-react'
import { TabularPreviewTable } from '@/components/tabular/TabularPreviewTable'
import type { ParquetSection, TabularColumn, TabularQueryRequest } from '@/services/api/lake'
import { QueryBuilder } from '../QueryBuilder'
import { SectionHeading } from './sections'

interface ParquetPreviewProps {
  parquetSections: ParquetSection[]
  selectedSectionIndex: number
  onSelectSection: (index: number) => void
  onClearParquetSection: () => void
  parquetColumns: TabularColumn[]
  parquetRows: (string | number | boolean | null)[][]
  parquetTotalRows: number
  parquetFilteredRows: number
  parquetCurrentOffset: number
  parquetLoading: boolean
  parquetNavigating: boolean
  parquetError: string | null
  onParquetQuery: (req: TabularQueryRequest) => Promise<void>
  onParquetPrevPage: () => Promise<void>
  onParquetNextPage: () => Promise<void>
}

export function ParquetPreview({
  parquetSections,
  selectedSectionIndex,
  onSelectSection,
  onClearParquetSection,
  parquetColumns,
  parquetRows,
  parquetTotalRows,
  parquetFilteredRows,
  parquetCurrentOffset,
  parquetLoading,
  parquetNavigating,
  parquetError,
  onParquetQuery,
  onParquetPrevPage,
  onParquetNextPage,
}: ParquetPreviewProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <SectionHeading
        icon={<Database size={13} style={{ color: 'var(--md-accent-text)' }} />}
        label={
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--md-text-muted)',
            }}
          >
            Parquet Preview
          </span>
        }
      />

      {parquetSections.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {parquetSections.map(section => {
            const isActive = section.index === selectedSectionIndex
            return (
              <button
                key={section.index}
                type="button"
                onClick={() => {
                  if (section.index === selectedSectionIndex) return
                  onSelectSection(section.index)
                  onClearParquetSection()
                }}
                style={{
                  padding: '4px 10px',
                  borderRadius: 'var(--md-r-sm)',
                  fontSize: 11,
                  fontWeight: 500,
                  border: '1px solid',
                  cursor: 'pointer',
                  background: isActive ? 'var(--md-accent-bg)' : 'var(--md-surface-raised)',
                  color: isActive ? 'var(--md-accent-text)' : 'var(--md-text-muted)',
                  borderColor: isActive ? 'var(--md-accent)' : 'var(--md-border)',
                  fontFamily: 'inherit',
                }}
              >
                {section.name}
              </button>
            )
          })}
        </div>
      )}

      {/* Stays mounted during loading: unmounting on each query would
          reset the builder's filter/sort/search state, hiding the
          applied filters and the "Clear all" button after Apply. */}
      {parquetColumns.length > 0 && (
        <QueryBuilder
          columns={parquetColumns}
          onQuery={onParquetQuery}
          totalRows={parquetTotalRows}
          filteredRows={parquetFilteredRows}
          isLoading={parquetLoading}
        />
      )}

      {parquetLoading && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '32px 0',
            borderRadius: 'var(--md-r-sm)',
            border: '1px solid var(--md-border)',
            background: 'var(--md-surface-raised)',
            color: 'var(--md-text-subtle)',
            fontSize: 12,
          }}
        >
          <Loader2 size={16} className="animate-spin" />
          Loading Parquet data…
        </div>
      )}

      {parquetError && !parquetLoading && (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 'var(--md-r-sm)',
            border: '1px solid var(--md-error)',
            background: 'var(--md-error-bg)',
            color: 'var(--md-error)',
            fontSize: 12,
          }}
        >
          {parquetError}
        </div>
      )}

      {!parquetLoading && !parquetError && parquetColumns.length > 0 && (
        <TabularPreviewTable
          columns={parquetColumns}
          rows={parquetRows}
          totalRows={parquetFilteredRows}
          currentOffset={parquetCurrentOffset}
          onPrevPage={onParquetPrevPage}
          onNextPage={onParquetNextPage}
          isNavigating={parquetNavigating}
          headerRowIndex={null}
          pendingRows={new Set()}
          pendingColumns={new Set()}
          selectionMode={null}
          onRowClick={() => {}}
          onColumnClick={() => {}}
          className="h-[50vh] flex flex-col overflow-hidden"
        />
      )}
    </div>
  )
}
